import React, { useMemo } from 'react';
import { useFarmerFormValidation } from '@farmerFormValidation/useFarmerFormValidation';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import breakpointSizes from '@applicationKYC/LegalEntityQuestionnaire/styled-theme-styles/global-style-variables.style';
import useWindowDimensions from '@applicationKYC/LegalEntityQuestionnaire/Utils/window-size.service';
import { Input, Field, Loader, PhoneField } from '@single-platform/components';
import {
  FormStyles,
  PageHeader
} from '@applicationKYC/LegalEntityQuestionnaire/screens/renderPage/renderPage.style';
import { ButtonSection } from '@applicationKYC/LegalEntityQuestionnaire/components';
import { useSelector } from 'react-redux';
import countries from '@auth/components/countriesDropdown/farmersCountryList.json';
import { DropdownInput } from '@farmerComponents/dropdownInput/dropdownInput';
import { ToastNotification } from '@farmerComponents/toastNotification/toastNotification';
import { ButtonSectionWrapperStyle } from '@applicationKYC/FarmersQuestionnaire/questionnaireSections/formStyles';
import { FarmersQuestionnaireHeader } from '@applicationKYC/FarmersQuestionnaire/farmersQuestionnaire.style';
import { selectKycAppState } from '@redux';

const defaultValues = {
  name: null,
  surname: null,
  countryOfBirth: null,
  countryOfResidence: null,
  taxNumber: null,
  email: null,
  phoneNumber: null
};

export const FarmerPage = ({ onSubmit, isAtBottom }) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { loading, farmerInformation } = useSelector((state) => state.FarmersQuestionnaireReducer);
  const applicationState = useSelector(selectKycAppState);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required(t('validation_required_field')).nullable(),
        surname: Yup.string().required(t('validation_required_field')).nullable(),
        countryOfBirth: Yup.string().required(t('validation_required_field')).nullable(),
        countryOfResidence: Yup.string().required(t('validation_required_field')).nullable(),
        taxNumber: Yup.string().required(t('validation_required_field')).nullable(),
        email: Yup.string()
          .email(t('validation_email_is_not_valid'))
          .required(t('validation_required_field'))
          .nullable(),
        phoneNumber: Yup.string().required(t('validation_required_field')).nullable()
      }),
    [t]
  );

  const existingValues = farmerInformation?.farmerSection?.farmerPage;
  const initialValues = { ...defaultValues, ...existingValues };

  const { validationHelpers, getValues, setTouchedOnAll, handleSubmit } = useFarmerFormValidation({
    initialValues,
    validationSchema
  });

  const onHandleSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(
      async (data) => {
        await onSubmit?.(data, 'farmerPage');
      },
      () => setTouchedOnAll
    )();
  };

  if (loading) return <Loader />;

  const values = getValues();

  return (
    <FormStyles small={width < breakpointSizes.phone}>
      <FarmersQuestionnaireHeader>
        <PageHeader>{t('farmers_farmer_info_header')}</PageHeader>
      </FarmersQuestionnaireHeader>
      <Field
        Component={Input}
        name="name"
        value={values.name}
        placeholder={t('farmers_farmer_name')}
        validationHelpers={validationHelpers}
        required
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <Field
        Component={Input}
        name="surname"
        value={values.surname}
        placeholder={t('farmers_farmer_surname')}
        validationHelpers={validationHelpers}
        required
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <DropdownInput
        validationHelpers={validationHelpers}
        name="countryOfBirth"
        value={values.countryOfBirth}
        filterCountries={false}
        placeholder={t('farmers_farmer_countryOfBirth')}
        options={countries}
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <DropdownInput
        validationHelpers={validationHelpers}
        name="countryOfResidence"
        value={values.countryOfResidence}
        filterCountries={false}
        placeholder={t('farmers_farmer_countryOfResidence')}
        options={countries}
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <Field
        Component={Input}
        type="number"
        name="taxNumber"
        value={values.taxNumber}
        placeholder={t('farmers_farmer_taxNumber')}
        validationHelpers={validationHelpers}
        required
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      {farmerInformation.farmRepresentativeSection && (
        <ToastNotification message={t('ceo_ondato_email_is_required')} />
      )}
      <Field
        Component={Input}
        name="email"
        value={values.email}
        placeholder={t('farmers_farmer_email')}
        validationHelpers={validationHelpers}
        required
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <Field
        Component={PhoneField}
        name="phoneNumber"
        value={values.phoneNumber}
        placeholder={t('farmers_farmer_phoneNumber')}
        validationHelpers={validationHelpers}
        required
        validateOnChange
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <ButtonSectionWrapperStyle isAtBottom={isAtBottom}>
        <ButtonSection
          secondButtonClick={onHandleSubmit}
          secondButtonPrimary={true}
          secondButtonText={
            applicationState?.state === 'UserFillingApplication'
              ? t('button_save_and_continue')
              : t('button_continue')
          }
        />
      </ButtonSectionWrapperStyle>
    </FormStyles>
  );
};
